// Clipboard-image paste support for the embedded terminal.
//
// The server has no clipboard, so a pasted image can never reach the Claude
// process as bytes-over-PTY. Instead: upload the image through the existing
// screenshot endpoint and type the stored file's ABSOLUTE path into the
// terminal — Claude reads the image from disk, exactly like image paths
// pasted in a real terminal.

export interface ImageItemLike {
  kind: string;
  type: string;
}

interface UploadResponse {
  ok: boolean;
  absolutePath?: string;
  error?: string;
}

export function findImageItem<T extends ImageItemLike>(
  items: ArrayLike<T> | undefined,
): T | undefined {
  if (!items) return undefined;
  return Array.from(items).find((it) => it.kind === "file" && it.type.startsWith("image/"));
}

// The endpoint enforces a PNG signature; normalize JPEG/WebP/… via canvas.
export async function toPngBlob(file: Blob): Promise<Blob> {
  if (file.type === "image/png") return file;
  const bmp = await createImageBitmap(file);
  try {
    const canvas = document.createElement("canvas");
    canvas.width = bmp.width;
    canvas.height = bmp.height;
    canvas.getContext("2d")!.drawImage(bmp, 0, 0);
    return await new Promise<Blob>((res, rej) => {
      canvas.toBlob((b) => (b ? res(b) : rej(new Error("canvas toBlob failed"))), "image/png");
    });
  } finally {
    bmp.close();
  }
}

function getCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]!) : null;
}

export async function uploadPastedImage(slug: string, file: Blob): Promise<string> {
  const png = await toPngBlob(file);
  const res = await fetch(`/api/projects/${encodeURIComponent(slug)}/screenshots`, {
    method: "POST",
    headers: {
      "content-type": "application/octet-stream",
      "x-csrf-token": getCookie("csrf_token") ?? "",
    },
    body: await png.arrayBuffer(),
  });
  const body = (await res.json().catch(() => ({}))) as UploadResponse;
  if (!res.ok || !body.ok || !body.absolutePath) {
    throw new Error(body.error ?? `upload failed (${res.status})`);
  }
  return body.absolutePath;
}
